import type { Folder, QuizSummary } from '@floorpoll/shared';

/** One heading on the dashboard. `folder` is null for the unfiled remainder. */
export interface FolderGroup {
  folder: Folder | null;
  quizzes: QuizSummary[];
}

/**
 * Story #76 — the grouping, done client-side over the poll list the dashboard
 * already fetches.
 *
 * Deliberately not a server concern: `listQuizzes` reads one GSI1 page
 * newest-first, and encoding a folder in that key would reorder the dashboard
 * and need a backfill. Folders cost one extra query per workspace, not one per
 * poll.
 *
 * Empty folders are kept. An author who has just made one needs to see it to
 * file anything into it, and needs it on the page to delete it again.
 */
export function groupQuizzesByFolder(quizzes: QuizSummary[], folders: Folder[]): FolderGroup[] {
  const known = new Set(folders.map((folder) => folder.folderId));
  const byFolder = new Map<string, QuizSummary[]>();
  const unfiled: QuizSummary[] = [];

  for (const quiz of quizzes) {
    // An id that resolves to nothing is what a deleted folder leaves behind:
    // `deleteFolder` removes one row and never rewrites the polls filed in it,
    // so rendering the dangling id as unfiled is the whole of that cleanup.
    if (!quiz.folderId || !known.has(quiz.folderId)) {
      unfiled.push(quiz);
      continue;
    }
    const bucket = byFolder.get(quiz.folderId);
    if (bucket) bucket.push(quiz);
    else byFolder.set(quiz.folderId, [quiz]);
  }

  const groups: FolderGroup[] = folders.map((folder) => ({
    folder,
    quizzes: byFolder.get(folder.folderId) ?? [],
  }));

  // Unfiled last: it is where a poll sits before anyone has filed it and where
  // one lands when its folder goes away, so it reads as the remainder rather
  // than as the first thing on the page.
  if (unfiled.length > 0) groups.push({ folder: null, quizzes: unfiled });
  return groups;
}
